import {useRef, useCallback, useEffect} from 'react'

// Global state to track user interaction across all instances
let globalHasUserInteracted = false
let globalAudioElement: HTMLAudioElement | null = null

/**
 * Custom hook for playing notification sounds
 * Uses HTML5 Audio API directly for better browser compatibility
 * Includes debounce to prevent multiple sounds playing at once (race condition)
 * Handles browser autoplay policy by tracking user interaction globally
 */
export const useNotificationSound = () => {
  const lastPlayedRef = useRef<number>(0)
  const debounceMs = 1000 // Only play sound once per second
  const isPlayingRef = useRef<boolean>(false)

  // Initialize audio element (shared globally) and set up user interaction listener
  useEffect(() => {
    // Only create audio element if it doesn't exist globally
    if (!globalAudioElement) {
      try {
        globalAudioElement = new Audio('/media/sounds/notification.mp3')
        globalAudioElement.volume = 0.5
        globalAudioElement.preload = 'auto'

        globalAudioElement.addEventListener('ended', () => {
          isPlayingRef.current = false
        })

        globalAudioElement.addEventListener('error', (e) => {
          console.warn('[Sound] Audio error:', e)
          isPlayingRef.current = false
        })

        // Preload the audio
        globalAudioElement.load()
        console.log('[Sound] Notification sound initialized (global)')
      } catch (error) {
        console.warn('[Sound] Failed to initialize audio:', error)
      }
    }

    // Unlock audio on user interaction
    const unlockAudio = () => {
      if (!globalHasUserInteracted && globalAudioElement) {
        globalHasUserInteracted = true
        console.log('[Sound] User interaction detected, unlocking audio...')

        // Play and immediately pause to unlock audio context
        const playPromise = globalAudioElement.play()
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              globalAudioElement?.pause()
              if (globalAudioElement) {
                globalAudioElement.currentTime = 0
              }
              console.log('[Sound] Audio unlocked successfully after user interaction')
            })
            .catch((error) => {
              console.warn('[Sound] Failed to unlock audio:', error.message)
              // Reset flag to try again on next interaction
              globalHasUserInteracted = false
            })
        }
      }
    }

    // Listen for user interaction events (multiple events for better coverage)
    const events = ['click', 'touchstart', 'keydown', 'mousedown']
    events.forEach((event) => {
      document.addEventListener(event, unlockAudio, {passive: true})
    })

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, unlockAudio)
      })
    }
  }, [])

  const playNotification = useCallback(() => {
    const now = Date.now()

    // Skip if audio not yet unlocked by user interaction
    if (!globalHasUserInteracted) {
      console.log('[Sound] Waiting for user interaction to unlock audio...')
      return
    }

    // Debounce check and ensure not already playing
    if (now - lastPlayedRef.current > debounceMs && !isPlayingRef.current) {
      lastPlayedRef.current = now
      isPlayingRef.current = true

      console.log('[Sound] Playing notification sound...')

      if (globalAudioElement) {
        // Reset to beginning in case it was already played
        globalAudioElement.currentTime = 0

        const playPromise = globalAudioElement.play()
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log('[Sound] Sound played successfully')
            })
            .catch((error) => {
              console.warn('[Sound] Failed to play sound:', error.message)
              isPlayingRef.current = false
            })
        }
      } else {
        console.warn('[Sound] Audio element not initialized')
        isPlayingRef.current = false
      }
    } else {
      console.log('[Sound] Debounced or already playing, skipping...')
    }
  }, [])

  // Force unlock method for manual triggering
  const forceUnlock = useCallback(() => {
    if (!globalHasUserInteracted && globalAudioElement) {
      globalHasUserInteracted = true
      console.log('[Sound] Force unlocking audio...')
    }
  }, [])

  return {
    playNotification,
    forceUnlock,
    isUnlocked: globalHasUserInteracted,
  }
}

export default useNotificationSound
